import { Fragment, useState } from "react";
import { toast } from "sonner";
import { Link } from "@tanstack/react-router";
import { PaymentTermsPanel } from "@/features/manufacturers/payment-terms-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { InlineFieldInput } from "@/components/inline-field-group";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { LoadingButton } from "@/components/loading-button";
import { ReplyAttachments } from "@/components/reply-attachments";
import { ReviewChoiceGroup } from "@/components/review-choice-group";
import { HandBookingForm } from "@/components/hand-booking-form";
import type { ReplyAttachmentDraft } from "@/features/communications/types";
import { PURCHASING_SIGNOFF, WARRANTY_SIGNOFF } from "@/features/communications/formatting";
import { ACTION_FOCUS_CLASS, actionTargetProps } from "@/hooks/use-action-focus";
import { AddActionPopover } from "./add-action-popover";
import { usePurchaseOrderInvoiceActions } from "./hooks";
import { POInvoiceActionRows } from "./invoice-actions";
import {
  Send,
  Check,
  X,
  Eye,
  Search,
  ArrowRightLeft,
  Trash2,
  Percent,
  DollarSign,
  Mail,
  UserCheck,
  Truck,
  Phone,
  ClipboardCheck,
  RefreshCw,
} from "lucide-react";
import {
  useRecordManufacturerOrder,
  useRecordTracking,
  useAddPoEmail,
  useResolveAction,
  useReopenAction,
  useResolveDeliverySite,
  useRemoveActionItem,
  useReplaceActionItem,
  useUpdateDiscountAction,
  useReplacementCandidates,
  useAcceptFee,
  useAddDeliveryServices,
  useResolveFreightModeAction,
  useApproveBackorder,
  useSendCustomerNotification,
  useDraftCustomerEmail,
  useSkipCustomerApproval,
  useRefundCustomer,
  useSendDraftResponse,
  useActionReplyRecipients,
} from "@/features/purchase-orders/hooks";
import { EmailRecipientsField } from "@/components/email-recipients-field";
import { useEmailRecipients } from "@/components/use-email-recipients";
import { useBookShipment, useEditShipment } from "@/api/hooks";
import { useRecordCommunication, useRetryMessage, useSendMessage } from "@/features/communications/hooks";
import { formatApiErrorMessage } from "@/lib/api-error";
import {
  cn,
  formatEventTime,
  formatMoney,
  formatStatus,
} from "@/lib/utils";
import { useActorName } from "@/hooks/use-actor-name";
import type { components } from "@/api/schema";
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  RotateCcw,
} from "lucide-react";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import type { PurchaseOrderDetail } from "./types";
import { isManufacturerSurchargeLine } from "./line-item-policy";
import { ActionAuditLine } from "./action-audit";
import {
  CustomerEmailComposer,
  CustomerEmailPreview,
  CustomerEmailSentLine,
} from "@/features/communications/customer-email-section";
import {
  ActionCardTop,
  ActionDetails,
  ActionFacts,
  ActionInvoiceLink,
  SourceEmailLink,
  SourceShipmentLink,
} from "./action-card-parts";
import { RecordPaymentPanel } from "@/components/record-payment-panel";
import { currentProgress, roleLabel } from "./action-card-model";
import { DelayNoticeChoice } from "./delay-notice-choice";
import { ActionPopover } from "@/components/action-popover";
import { DismissActionButton } from "./dismiss-action-button";
import { RefundOrCreditChoices } from "./refund-or-credit";
import { SourceChangeForm } from "./source-change-form";
import {
  actionAuditEvents,
  actionResolutionNotes,
  type PurchaseOrderActionReopen,
} from "./action-audit-model";
import { ActionRecoveryForm, MoreFixesMenu } from "./action-recovery";
import {
  useRecoveryPopover,
  type ActionPopoverConfig,
} from "./action-recovery-popover";
import { ActionWorkSection, type ActionCommentsContext } from "./action-work";
import { useActionItemSource } from "./action-source";
import { useActionOptionRenderer } from "./action-option-context";
import { ActionCardChaseActions } from "@/features/owed-items/chase-actions";
import { ExpediteCallOutcome } from "@/features/purchase-orders/expedite-call-outcome";
import {
  isPopoverRecovery,
  RECOVERY_OPTION_MODES,
  type RecoveryMode,
  type RecoveryPrefill,
} from "./action-recovery-modes";
import { SearchInput } from "@/components/search-input";
import { ComposeTextarea } from "@/components/compose-field";

/** The sign-off the server appends to every action reply, shown where the reply is written.
 * Customer replies go out in the branded order email, which carries its own footer; data@ replies
 * (USA-1474) are not signed by purchasing, and warranty@ replies carry the warranty sign-off (USA-915). */
function SignoffPreview() {
  const kind = useActionItemSource().kind;
  const signoff =
    kind === "purchase_order" ? PURCHASING_SIGNOFF : kind === "warranty_claim" ? WARRANTY_SIGNOFF : null;
  if (!signoff) return null;
  return (
    <p className="whitespace-pre-line text-xs text-muted-foreground">
      {signoff}
    </p>
  );
}

/** The ``action_source`` the priority picker saves under, by where the item lives. */
const PRIORITY_SOURCE = {
  purchase_order: "purchase_order",
  customer_thread: "customer_communication",
  manufacturer_data: "manufacturer_data",
  warranty_claim: "warranty_claim",
} as const;

/** Who a reply on a warranty item goes to: whoever wrote the email that raised it (USA-915). */
const WARRANTY_COUNTERPARTY: Record<string, string> = {
  customer_question: "customer",
  unmatched_email: "sender",
};

function actionOwners(
  action: PurchaseOrderAction,
  shares: readonly PurchaseOrderActionShare[],
) {
  const roles = [
    action.assigned_role,
    ...shares.filter((share) => share.action_id === action.id).map((share) => share.role),
  ].filter((role): role is string => !!role);
  return [...new Set(roles.map(roleLabel))];
}

type PurchaseOrderAction = NonNullable<
  components["schemas"]["PurchaseOrderDetailRead"]["actions"]
>[number];

type PurchaseOrderActionShare =
  components["schemas"]["PurchaseOrderActionShareRead"];
type ActionResolutionOption = {
  option_type: string;
  title?: string;
  reply_body?: string;
  recipient_email?: string;
  recipient_name?: string;
};

const ACTION_OPTION_LABELS: Record<string, string> = {
  cancel_po: "Email manufacturer & cancel PO",
  approve_delay: "Wait for the new date",
  request_firm_eta: "Request a firm date",
  remove_item: "Remove item & refund",
  replace_item: "Find replacement",
  update_discount: "Apply corrected pricing",
  dispute_price: "Dispute pricing",
  request_quote: "Request a corrected quote",
  accept_fee: "Accept fee",
  dispute_fee: "Dispute fee",
  request_fee_waiver: "Request fee waiver",
  decline_expedite: "Keep current ship date",
  usars_books_freight: "USA-RS books freight",
  manufacturer_books_freight: "They book, add to invoice",
  use_our_ups_account: "Use our UPS account",
  switch_to_ltl: "Switch to LTL",
  switch_to_parcel: "Switch to small parcel",
  change_source: "Order through another source",
  send_requested_information: "Send requested information",
  request_missing_information: "Request missing information",
  payment_submitted: "Confirm payment submitted",
  request_payment_instructions: "Request payment instructions",
  follow_up_spec_credit: "Follow up on spec credit",
  revise_line_item: "Revise line",
  record_cancellation: "Record cancellation",
  redirect_contact: "Send PO to another contact",
  accept_freight_charge: "Accept prepaid & add freight",
  add_delivery_services: "Add to our booking",
  keep_delivery_services_off: "Leave it off",
  reply_to_manufacturer: "Reply to the manufacturer",
  set_lead_time: "Set the lead time",
  keep_current_data: "Keep what we have",
  stop_lead_time_checks: "Stop the weekly check",
  keep_lead_time_checks: "Keep the weekly check",
  record_terms: "Record the terms myself",
  call_manufacturer: "Call the manufacturer",
  apply_price_sheet: "Apply the sheet",
  discard_price_sheet: "Discard the sheet",
  approve_tax_exemption: "Approve form",
  decline_tax_exemption: "Reject form",
  email_customer: "Email customer",
  custom_reply: "Send a custom reply",
};

/** A tax-exempt form's review choices (USA-1504): carried out on the form's page. */
const TAX_FORM_OPTION_TYPES = new Set([
  "approve_tax_exemption",
  "decline_tax_exemption",
  "email_customer",
]);

const FREIGHT_MODE_NAMES = { ltl: "LTL", parcel: "small parcel" } as const;

const CLOSED_RESOLUTION_LABELS: Record<string, string> = {
  switch_to_ltl: "Switched to LTL",
  switch_to_parcel: "Switched to small parcel",
  keep_ltl: "Kept LTL",
  keep_parcel: "Kept small parcel",
  owed_called: "Called",
  change_source: "Source changed",
  expedite_called: "Called",
  check_in_sent: "Check-in sent",
};

/**
 * A freight mode action decides LTL or small parcel (USA-1401): switching comes first, keeping
 * the PO's mode second. A choice the AI drafted a reply for keeps it, so the reply goes out with
 * the change (USA-1658). Where the card can't change the PO's freight (a customer thread) the
 * choices are left off.
 */
function freightModeOptions(
  options: ActionResolutionOption[],
  actionType: string,
  freightMode: "parcel" | "ltl" | null | undefined,
): ActionResolutionOption[] {
  const others = options.filter(
    (option) => !option.option_type.startsWith("switch_to_"),
  );
  if (actionType !== "freight_mode" || freightMode === undefined) return others;
  const modes: ("ltl" | "parcel")[] =
    freightMode === "ltl" ? ["parcel", "ltl"] : ["ltl", "parcel"];
  return [
    ...modes.map((mode) => {
      const drafted = options.find((option) => option.option_type === `switch_to_${mode}`);
      return {
        ...(drafted?.reply_body ? { reply_body: drafted.reply_body } : {}),
        option_type: `switch_to_${mode}`,
        title:
          mode === freightMode
            ? `Keep ${FREIGHT_MODE_NAMES[mode]}`
            : `Switch to ${FREIGHT_MODE_NAMES[mode]}`,
      };
    }),
    ...others,
  ];
}

/**
 * The PO's freight mode as its action cards see it: a supplier on our UPS account ships small
 * parcels, and LTL there is ours to book (USA-1658). Other freight the manufacturer books has no
 * mode to choose.
 */
function cardFreightMode(po: PurchaseOrderDetail): "parcel" | "ltl" | null | undefined {
  if (po.we_book_freight) return po.freight_mode ?? null;
  return po.freight_billing === "USA-RS UPS account" ? "parcel" : undefined;
}

/**
 * A source change action (USA-1506) leads with buying the PO through the company the supplier
 * named. Where the card can't change the PO's source (a customer thread) the choice is left off.
 */
function sourceChangeOptions(
  options: ActionResolutionOption[],
  actionType: string,
  canChangeSource: boolean,
  requiredSource: string | null,
): ActionResolutionOption[] {
  const others = options.filter((option) => option.option_type !== "change_source");
  if (actionType !== "source_change" || !canChangeSource) return others;
  return [
    {
      option_type: "change_source",
      title: requiredSource ? `Order through ${requiredSource}` : "Order through another source",
    },
    ...others,
  ];
}

/** The send button of an automatic customer email held for review (USA-1413). */
const HELD_EMAIL_SEND_LABELS: Record<string, string> = {
  order_hold_verification: "Send ID verification",
  shipment_delay: "Send delay notice",
  shipment_exception: "Send shipment update",
};

/** The money goes straight back to how the customer paid, so it is confirmed first (USA-1693). */
function RefundCustomerOption({
  poId,
  actionId,
  label,
  variant,
  disabled,
}: {
  poId: string;
  actionId: number;
  label: string;
  variant: "default" | "outline";
  disabled: boolean;
}) {
  const refund = useRefundCustomer(poId);
  return (
    <ActionPopover
      trigger={<Button size="sm" variant={variant} disabled={disabled}>{label}</Button>}
      title={`${label} to the customer?`}
      confirmLabel={label}
      pending={refund.isPending}
      pendingLabel="Refunding..."
      onConfirm={(_note, close) => refund.mutate({ actionId }, { onSuccess: close })}
    />
  );
}

function StructuredActionCard({
  action,
  poId,
  freightMode,
  manufacturer,
  lineItems,
  customerName,
  customerEmail,
  onSkipSpecCredit,
  skippingSpecCredit = false,
  reopens,
  shares,
  underSourceEmail = false,
  flat = false,
  comments,
}: {
  action: PurchaseOrderAction;
  poId: string;
  /** Where its comments live; without a project the card has no comments. */
  comments?: ActionCommentsContext;
  /** Shown under the email that raised it, which needs no link back to itself. */
  underSourceEmail?: boolean;
  /** Drawn without its own box, inside a record's row that already is one (a warranty claim, USA-915). */
  flat?: boolean;
  /** The PO's freight mode; set only where the card can change the PO's freight. */
  freightMode?: "parcel" | "ltl" | null;
  /** The PO's manufacturer; set only where the card can change where the PO is bought. */
  manufacturer?: { uuid: string; name: string };
  reopens: readonly PurchaseOrderActionReopen[];
  shares: readonly PurchaseOrderActionShare[];
  lineItems: components["schemas"]["PurchaseOrderDetailRead"]["line_items"];
  customerName?: string | null;
  customerEmail?: string | null;
  onSkipSpecCredit?: () => void;
  skippingSpecCredit?: boolean;
}) {
  const actionSource = useActionItemSource();
  const [mode, setMode] = useState<
    "choose" | "remove" | "replace" | "discount" | "fee" | null
  >(null);
  const [recoveryMode, setRecoveryMode] = useState<RecoveryMode | null>(null);
  // Freight our team booked by hand for a failed booking (USA-1455).
  const [handBooking, setHandBooking] = useState(false);
  // Buying the PO through the source the supplier named (USA-1506).
  const [changingSource, setChangingSource] = useState(false);
  // Recording a call to expedite a Service PO (USA-1192).
  const [recordingExpediteCall, setRecordingExpediteCall] = useState(false);
  const [recoveryPrefill, setRecoveryPrefill] = useState<
    RecoveryPrefill | undefined
  >(undefined);
  const structuredData = (() => {
    try {
      return action.structured_data ? JSON.parse(action.structured_data) : null;
    } catch {
      return null;
    }
  })();
  const suggestedModel =
    typeof structuredData?.suggested_model === "string"
      ? structuredData.suggested_model
      : "";
  const suggestedPrice =
    structuredData?.suggested_price != null
      ? String(structuredData.suggested_price)
      : "";
  const [searchTerm, setSearchTerm] = useState(suggestedModel);
  const [debouncedSearch, setDebouncedSearch] = useState(suggestedModel);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null,
  );
  const [priceOverride, setPriceOverride] = useState(suggestedPrice);
  const suggestedDiscount =
    typeof structuredData?.discount_chain === "string"
      ? structuredData.discount_chain
      : "";
  const [discountChain, setDiscountChain] = useState(suggestedDiscount);
  const [notes, setNotes] = useState("");
  const [feeDescription, setFeeDescription] = useState(
    (typeof structuredData?.charge_type === "string"
      ? structuredData.charge_type
      : "") || "Additional Charge",
  );
  const [feeAmount, setFeeAmount] = useState(
    action.charge_amount?.toString() || "",
  );
  const [selectedLineItemId, setSelectedLineItemId] = useState<number | null>(
    action.line_item_id ?? null,
  );

  const [draftBody, setDraftBody] = useState(action.draft_response || "");
  // Recording the payment of the invoice an accounting item is about (USA-103).
  const [paying, setPaying] = useState(false);
  // The AI's drafted reply stays folded until staff choose to review it.
  const [showDraftReply, setShowDraftReply] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [attachments, setAttachments] = useState<ReplyAttachmentDraft[]>([]);
  const [attaching, setAttaching] = useState(false);
  const [selectedResolutionOption, setSelectedResolutionOption] =
    useState<ActionResolutionOption | null>(null);
  const [deliverySiteChoice, setDeliverySiteChoice] = useState<
    "commercial" | "residential" | null
  >(null);

  const actorName = useActorName();
  const raisedBy = actorName(action.created_by);
  const itemSource = useActionItemSource();
  const renderOption = useActionOptionRenderer();
  const counterparty =
    itemSource.kind === "customer_thread"
      ? "customer"
      : itemSource.kind === "warranty_claim"
        ? (WARRANTY_COUNTERPARTY[action.action_type] ?? "manufacturer")
        : "manufacturer";

  const removeMutation = useRemoveActionItem(poId);
  const replaceMutation = useReplaceActionItem(poId);
  const discountMutation = useUpdateDiscountAction(poId);
  const feeMutation = useAcceptFee(poId);
  const approveBackorderMutation = useApproveBackorder(poId);
  const resolveActionMutation = useResolveAction(poId);
  const addDeliveryServicesMutation = useAddDeliveryServices(poId);
  const freightModeMutation = useResolveFreightModeAction(poId);
  const resolveDeliverySiteMutation = useResolveDeliverySite(poId);
  const recordManufacturerOrderMutation = useRecordManufacturerOrder(poId);
  const recordTrackingMutation = useRecordTracking(poId);
  const addPoEmailMutation = useAddPoEmail(poId);
  const recordCallMutation = useRecordCommunication(poId);
  const sendCustomerNotificationMutation = useSendCustomerNotification(poId);
  const draftCustomerEmailMutation = useDraftCustomerEmail(poId);
  const skipCustomerApprovalMutation = useSkipCustomerApproval(poId);
  const sendDraftResponseMutation = useSendDraftResponse(poId);
  const retryReplyMutation = useRetryMessage();
  const sendHeldEmail = useSendMessage();
  const bookShipmentMutation = useBookShipment(poId);
  const editShipmentMutation = useEditShipment(poId);

  const { data: candidates, isLoading: candidatesLoading } =
    useReplacementCandidates(poId, action.id, debouncedSearch);

  // Debounce search input
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    // Simple debounce using setTimeout
    const timer = setTimeout(() => setDebouncedSearch(value), 300);
    return () => clearTimeout(timer);
  };

  const linkedLineItem = lineItems?.find(
    (li) => li.id === (selectedLineItemId ?? action.line_item_id),
  );

  // A due date already shown as one of the facts isn't repeated in the header.
  const headerDue =
    action.expected_date &&
    !action.card?.facts.some(
      (fact) => fact.kind === "date" && fact.value === action.expected_date,
    )
      ? action.expected_date
      : null;

  const isBackorderAction = action.action_type === "backorder";
  const isLineItemActionType =
    action.action_type === "discontinued" ||
    action.action_type === "substitution";

  const isDiscountAction = action.action_type === "price_change";
  const isFeeAction =
    action.action_type === "additional_charge" ||
    action.action_type === "payment_charge";
  const isSpecCreditNoResponse =
    action.action_type === "spec_credit_no_response";
  // Opened and cleared by the hold itself; the header's Release hold is the only way out.
  const isOperationalHold = action.action_type === "operational_hold";
  // Sales's "Review POs" (USA-1333): closes itself once every PO is reviewed on the project.
  const poReviewProjectUuid =
    action.action_type === "po_review" && typeof structuredData?.project_uuid === "string"
      ? structuredData.project_uuid
      : null;
  const isSystemOwned = isOperationalHold || action.action_type === "po_review";
  // Closed by recording the manufacturer's terms, here or on the manufacturer page.
  const termsManufacturerUuid =
    action.action_type === "manufacturer_terms_unknown" &&
    action.status === "open" &&
    typeof structuredData?.manufacturer_uuid === "string"
      ? structuredData.manufacturer_uuid
      : null;
  const isDeliverySiteReview =
    action.action_type === "delivery_site_review" &&
    action.created_by === "delivery_site_verification";
  const isPoNotSent = action.action_type === "po_not_sent";
  // The manufacturer has no PO email; adding one sends this PO and its other waiting POs.
  const missingPoEmailFor =
    isPoNotSent &&
    action.status === "open" &&
    structuredData?.missing_po_email === true
      ? typeof structuredData.manufacturer_name === "string"
        ? structuredData.manufacturer_name
        : "this manufacturer"
      : null;
  // The supplier's mailbox is unmonitored; the email agent closes this from the recorded call.
  // A Service order's stuck shipping is a call to make too (USA-1604): recording the call closes it.
  const isSupplierCall =
    action.action_type === "supplier_call" || action.action_type === "expedite_shipping_call";
  // A Service order's day-after-delivery email, written by its own person (USA-1604).
  const isPersonalCheckIn = action.action_type === "delivery_personal_check_in";
  // A Service PO just went out (USA-1192): recording the call closes it.
  const isExpediteCall = action.action_type === "expedite_order_call";
  // A shipment with no number to track (USA-1554): typing the number closes it.
  const isTrackingMissing = action.action_type === "tracking_missing";
  // The AI's reply that never went out (USA-1654): Retry send sends it and closes the item.
  const unsentReplyUuid =
    action.action_type === "email_delivery_failed" &&
    action.status === "open" &&
    typeof structuredData?.outbound_message_uuid === "string"
      ? structuredData.outbound_message_uuid
      : null;
  // A store customer's tax-exempt form (USA-1504): approved or declined, never answered by reply.
  // Dismissing its review would leave the form undecided, so it has no Dismiss.
  const isTaxFormReview = action.action_type === "tax_exemption_review";
  // The review is never answered by writing to the customer from the card.
  const isTaxFormItem = isTaxFormReview;
  const taxFormUuid =
    typeof structuredData?.tax_exemption_uuid === "string" ? structuredData.tax_exemption_uuid : null;
  // A late owed item (USA-1423): chased with a new date, received, or no longer needed.
  const isOwedOverdue = action.action_type === "owed_overdue";
  // A cancelled PO we already paid for (USA-1471): ask for the money back, or record it came back.
  const isRefundOrCredit = action.action_type === "refund_or_credit";
  // Sales chooses whether the customer hears about a delay (USA-1426).
  const isDelayNotice = action.action_type === "customer_delay_notice";
  const bookShipmentId =
    action.action_type === "booking_issue" &&
    action.status === "open" &&
    typeof structuredData?.shipment_id === "number"
      ? (structuredData.shipment_id as number)
      : null;
  // The invoice this accounting item asks us to pay; Record payment takes the place of a "payment submitted" reply.
  const payableInvoice =
    action.status === "open" && action.card?.invoice?.record_payment ? action.card.invoice : null;
  const requiredSource =
    typeof structuredData?.facts?.required_source === "string"
      ? (structuredData.facts.required_source as string)
      : null;
  const resolutionOptions = sourceChangeOptions(freightModeOptions(
    Array.isArray(structuredData?.resolution_options)
      ? structuredData.resolution_options.filter(
          (option: unknown): option is ActionResolutionOption =>
            typeof option === "object" &&
            option !== null &&
            typeof (option as ActionResolutionOption).option_type === "string" &&
            // Retired: checking back is not a decision, so it never closes an item (USA-1519).
            (option as ActionResolutionOption).option_type !== "schedule_follow_up" &&
            !(payableInvoice && (option as ActionResolutionOption).option_type === "payment_submitted"),
        )
      : [],
    action.action_type,
    freightMode,
  ), action.action_type, manufacturer !== undefined, requiredSource);

  // Who the card's reply goes to, changeable before it is sent (USA-1663).
  const draftReplyOpen =
    Boolean(action.draft_response) &&
    action.status === "open" &&
    !paying &&
    resolutionOptions.length === 0 &&
    showDraftReply;
  const replyRecipientsQuery = useActionReplyRecipients(
    poId,
    action.id,
    selectedResolutionOption !== null || draftReplyOpen,
  );
  const replyRecipients = useEmailRecipients(
    replyRecipientsQuery.data ?? (replyRecipientsQuery.isError ? { to: [], cc: [] } : undefined),
  );

  const isAwaitingCustomer =
    action.customer_approval_required &&
    action.customer_approval_status === "sent" &&
    !action.customer_decision;
  const hasCustomerDecision =
    action.customer_approval_required && action.customer_decision != null;
  const hasDraftReady =
    action.customer_approval_required &&
    action.customer_approval_status === "draft_ready";
  const customerEmailStep = action.card?.customer_email;
  const heldEmail =
    action.status === "open" &&
    customerEmailStep?.send_via === "customer_message" &&
    customerEmailStep.message_uuid &&
    (customerEmailStep.status === "draft" || customerEmailStep.status === "failed")
      ? customerEmailStep
      : null;

  const anyPending =
    attaching ||
    retryReplyMutation.isPending ||
    skippingSpecCredit ||
    removeMutation.isPending ||
    replaceMutation.isPending ||
    discountMutation.isPending ||
    feeMutation.isPending ||
    approveBackorderMutation.isPending ||
    sendCustomerNotificationMutation.isPending ||
    draftCustomerEmailMutation.isPending ||
    skipCustomerApprovalMutation.isPending ||
    sendDraftResponseMutation.isPending ||
    resolveActionMutation.isPending ||
    addDeliveryServicesMutation.isPending ||
    freightModeMutation.isPending ||
    resolveDeliverySiteMutation.isPending ||
    recordManufacturerOrderMutation.isPending ||
    recordTrackingMutation.isPending ||
    addPoEmailMutation.isPending ||
    recordCallMutation.isPending ||
    bookShipmentMutation.isPending ||
    editShipmentMutation.isPending;

  // Replace, revise and redirect open inline under the buttons; the quick fixes open in one
  // anchored popover instead, so the buttons stay put while it is open (USA-1536).
  const inlineMode = mode === "choose" || mode === "replace";
  const inlinePanelOpen =
    inlineMode || recoveryMode === "revise" || recoveryMode === "redirect";
  const popoverMode =
    mode === "fee" || mode === "remove" || mode === "discount"
      ? mode
      : isPopoverRecovery(recoveryMode)
        ? recoveryMode
        : null;
  // The last popover keeps its content while it animates closed.
  const [shownPopover, setShownPopover] = useState(popoverMode);
  if (popoverMode !== null && popoverMode !== shownPopover) {
    setShownPopover(popoverMode);
  }
  const recoveryPopover = useRecoveryPopover({
    mode: isPopoverRecovery(shownPopover) ? shownPopover : null,
    open: isPopoverRecovery(recoveryMode),
    action,
    poId,
    onClose: () => setRecoveryMode(null),
  });
  const closePopover = () => {
    if (mode === "remove") setSelectedLineItemId(action.line_item_id ?? null);
    if (mode === "fee" || mode === "remove" || mode === "discount") setMode(null);
    else if (isPopoverRecovery(recoveryMode)) setRecoveryMode(null);
  };
  const notesField = (
    <ComposeTextarea
      aria-label="Notes"
      placeholder="Notes (optional)"
      value={notes}
      onChange={(e) => setNotes(e.target.value)}
      rows={2}
    />
  );
  const removesOnlyItem =
    linkedLineItem?.item_number === 1 && (lineItems?.length ?? 0) <= 1;
  const popover: ActionPopoverConfig | null =
    shownPopover === "fee"
      ? {
          title: "Accept fee",
          children: (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label
                    htmlFor={`fee-description-${action.id}`}
                    className="text-xs text-muted-foreground"
                  >
                    Description
                  </label>
                  <InlineFieldInput
                    id={`fee-description-${action.id}`}
                    placeholder="e.g. Small Order Charge"
                    value={feeDescription}
                    onChange={(e) => setFeeDescription(e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <label
                    htmlFor={`fee-amount-${action.id}`}
                    className="text-xs text-muted-foreground"
                  >
                    Amount ($)
                  </label>
                  <InlineFieldInput
                    id={`fee-amount-${action.id}`}
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={feeAmount}
                    onChange={(e) => setFeeAmount(e.target.value)}
                  />
                </div>
              </div>
              {notesField}
            </>
          ),
          confirmLabel: "Confirm fee",
          pending: feeMutation.isPending,
          pendingLabel: "Accepting...",
          canConfirm:
            Boolean(feeDescription.trim()) &&
            Boolean(feeAmount) &&
            Number(feeAmount) > 0 &&
            !anyPending,
          onConfirm: () =>
            feeMutation.mutate(
              {
                actionId: action.id,
                feeDescription,
                feeAmount: Number(feeAmount),
                notes: notes || undefined,
              },
              { onSuccess: () => setMode(null) },
            ),
        }
      : shownPopover === "remove"
        ? {
            title: "Remove item",
            children:
              !linkedLineItem && lineItems && lineItems.length > 0 ? (
                <>
                  <p className="text-xs text-muted-foreground">
                    Which line item is affected?
                  </p>
                  <div className="max-h-60 space-y-1 overflow-y-auto">
                    {lineItems.map((li) => (
                      <button
                        key={li.id}
                        type="button"
                        onClick={() => setSelectedLineItemId(li.id)}
                        className={`w-full text-left rounded-md border px-3 py-2 text-sm hover:bg-accent ${selectedLineItemId === li.id ? "border-primary bg-primary/5" : ""}`}
                      >
                        #{li.item_number}: {li.model} — qty {li.quantity},{" "}
                        {formatMoney(li.line_total)}
                      </button>
                    ))}
                  </div>
                </>
              ) : linkedLineItem ? (
                <>
                  <p className="text-sm break-words">
                    {linkedLineItem.model} · qty {linkedLineItem.quantity} ·{" "}
                    {formatMoney(linkedLineItem.line_total)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Refunds the customer and notifies the manufacturer.
                    {removesOnlyItem &&
                      " The PO will be cancelled since this is the only item."}
                  </p>
                  {notesField}
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No line items on this PO.
                </p>
              ),
            confirmLabel: lineItems?.length ? "Confirm removal" : undefined,
            confirmVariant: "destructive",
            pending: removeMutation.isPending,
            pendingLabel: "Removing...",
            canConfirm: Boolean(linkedLineItem) && !anyPending,
            onConfirm: lineItems?.length
              ? () =>
                  removeMutation.mutate(
                    {
                      actionId: action.id,
                      lineItemId: selectedLineItemId ?? undefined,
                      notes: notes || undefined,
                    },
                    { onSuccess: () => setMode(null) },
                  )
              : undefined,
          }
        : shownPopover === "discount"
          ? {
              title: "Update discount chain",
              children: (
                <>
                  <InlineFieldInput
                    aria-label="Discount chain"
                    placeholder="e.g. 50/10/5"
                    value={discountChain}
                    onChange={(e) => setDiscountChain(e.target.value)}
                  />
                  {notesField}
                </>
              ),
              confirmLabel: "Apply discount",
              pending: discountMutation.isPending,
              pendingLabel: "Updating...",
              canConfirm: Boolean(discountChain.trim()) && !anyPending,
              onConfirm: () =>
                discountMutation.mutate(
                  {
                    actionId: action.id,
                    newDiscountChain: discountChain,
                    notes: notes || undefined,
                  },
                  { onSuccess: () => setMode(null) },
                ),
            }
          : recoveryPopover;

  const emailCustomerButton =
    action.status === "open" && hasDraftReady && customerEmailStep && !emailOpen && !isDelayNotice ? (
      <Button
        size="sm"
        variant={isPersonalCheckIn ? "default" : "outline"}
        onClick={() => setEmailOpen(true)}
        disabled={anyPending}
      >
        <Mail className="mr-1 h-3 w-3" /> {isPersonalCheckIn ? "Review and send" : "Email customer"}
      </Button>
    ) : null;

  // A pallet booked as a parcel fails for size; the fix is freight (USA-1455).
  const switchToLtl = bookShipmentId !== null && structuredData?.failure_kind === "too_big";
  const bookNow = (shipmentId: number) =>
    bookShipmentMutation.mutate(shipmentId, {
      onSuccess: () => toast.success("Booking queued — the label or BOL email follows"),
      onError: (err) => toast.error(formatApiErrorMessage("Not booked", err)),
    });
  const bookingButtons =
    bookShipmentId !== null ? (
      <>
        <LoadingButton
          size="sm"
          variant={resolutionOptions.length > 0 && !switchToLtl ? "outline" : "default"}
          loading={bookShipmentMutation.isPending || editShipmentMutation.isPending}
          loadingText="Booking..."
          disabled={anyPending}
          onClick={() =>
            switchToLtl
              ? editShipmentMutation.mutate(
                  { shipmentId: bookShipmentId, body: { freight_mode: "ltl" } },
                  {
                    onSuccess: () => bookNow(bookShipmentId),
                    onError: (err) => toast.error(formatApiErrorMessage("Not switched", err)),
                  },
                )
              : bookNow(bookShipmentId)
          }
        >
          <Truck className="mr-1 h-3 w-3" /> {switchToLtl ? "Switch to LTL and book" : "Book shipment"}
        </LoadingButton>
        <Button size="sm" variant="outline" onClick={() => setHandBooking(true)} disabled={anyPending}>
          <ClipboardCheck className="mr-1 h-3 w-3" /> Booked by hand
        </Button>
      </>
    ) : null;

  const chooseResolutionOption = (option: ActionResolutionOption) => {
    // A manufacturer data choice with no reply (set the lead time, stop the weekly check) is carried out at once,
    // and so is a warranty item's (USA-915).
    if (
      (itemSource.kind === "manufacturer_data" || itemSource.kind === "warranty_claim") &&
      !option.reply_body &&
      option.option_type !== "custom_reply"
    ) {
      sendDraftResponseMutation.mutate(
        { actionId: action.id, bodyText: "", resolutionType: option.option_type },
        {
          onSuccess: () => toast.success(`${option.title || ACTION_OPTION_LABELS[option.option_type] || "Done"} · item closed`),
          onError: (err) => toast.error(formatApiErrorMessage("Not done", err)),
        },
      );
      return;
    }
    if (option.option_type === "remove_item") {
      setMode("remove");
      return;
    }
    if (option.option_type === "replace_item") {
      setMode("replace");
      return;
    }
    if (option.option_type === "update_discount") {
      setMode("discount");
      return;
    }
    if (option.option_type === "accept_fee") {
      setMode("fee");
      return;
    }
    if (option.option_type === "approve_delay") {
      approveBackorderMutation.mutate({ actionId: action.id });
      return;
    }
    if (option.option_type === "add_delivery_services") {
      addDeliveryServicesMutation.mutate(
        { actionId: action.id },
        {
          onSuccess: () => toast.success("Added to the shipment"),
          onError: (error) =>
            toast.error(formatApiErrorMessage("Not added", error)),
        },
      );
      return;
    }
    if (
      (option.option_type === "switch_to_ltl" ||
        option.option_type === "switch_to_parcel") &&
      !option.reply_body
    ) {
      const nextMode = option.option_type === "switch_to_ltl" ? "ltl" : "parcel";
      freightModeMutation.mutate(
        { actionId: action.id, freightMode: nextMode },
        {
          onSuccess: () =>
            toast.success(`Freight mode set to ${FREIGHT_MODE_NAMES[nextMode]}`),
          onError: (error) =>
            toast.error(formatApiErrorMessage("Freight mode not changed", error)),
        },
      );
      return;
    }
    if (option.option_type === "change_source") {
      setChangingSource(true);
      return;
    }
    if (option.option_type === "customer_refunded") {
      resolveActionMutation.mutate(
        { actionId: action.id, status: "resolved", notes: "Refunded the customer" },
        { onSuccess: () => toast.success("Marked refunded") },
      );
      return;
    }
    if (option.option_type === "keep_delivery_services_off") {
      resolveActionMutation.mutate({
        actionId: action.id,
        status: "resolved",
        notes: "Left off the freight we book",
      });
      return;
    }
    const recovery = RECOVERY_OPTION_MODES[option.option_type];
    if (recovery) {
      openRecovery(recovery, {
        recipientEmail: option.recipient_email,
        recipientName: option.recipient_name,
        replyBody: option.reply_body,
      });
      return;
    }
    setRecoveryMode(null);
    setSelectedResolutionOption(option);
    setDraftBody(option.reply_body || "");
  };

  function openRecovery(nextMode: RecoveryMode, prefill?: RecoveryPrefill) {
    setSelectedResolutionOption(null);
    setMode(null);
    setRecoveryPrefill(prefill);
    setRecoveryMode(nextMode);
  }

  // A backorder or substitution can be answered by cancelling the PO with its manufacturer; a few other
  // supplier problems offer it under More fixes (USA-1693). Skipped when the AI already offered it.
  const cancelPoOption: ActionResolutionOption | null =
    action.status === "open" &&
    action.card?.cancel_po_draft &&
    !resolutionOptions.some((option) => option.option_type === "cancel_po")
      ? {
          option_type: "cancel_po",
          title: ACTION_OPTION_LABELS.cancel_po,
          reply_body: action.card.cancel_po_draft,
        }
      : null;
  const cancelPoAsButton =
    action.action_type === "backorder" || action.action_type === "substitution";
  const cancelPoButton =
    cancelPoOption && cancelPoAsButton ? (
      <Button
        size="sm"
        variant="outline"
        onClick={() => chooseResolutionOption(cancelPoOption)}
        disabled={anyPending}
      >
        <Mail className="mr-1 h-3 w-3" /> {ACTION_OPTION_LABELS.cancel_po}
      </Button>
    ) : null;
  const cancelPoInMenu =
    cancelPoOption && !cancelPoAsButton
      ? () => chooseResolutionOption(cancelPoOption)
      : undefined;

  const addPoEmail = (email: string, close: () => void) => {
    if (!email) return;
    addPoEmailMutation.mutate(
      { actionId: action.id, email },
      {
        onSuccess: (result) => {
          close();
          const count = result?.resumed_count ?? 0;
          toast.success(
            count > 0
              ? `Sending ${count} PO${count === 1 ? "" : "s"} to ${email}`
              : `${email} saved`,
          );
        },
        onError: (error) =>
          toast.error(formatApiErrorMessage("Email not saved", error)),
      },
    );
  };

  const recordManufacturerOrder = (orderNumber: string, close: () => void) => {
    if (!orderNumber) return;
    recordManufacturerOrderMutation.mutate(
      { actionId: action.id, orderNumber },
      {
        onSuccess: () => {
          close();
          toast.success(`Order #${orderNumber} recorded`);
        },
        onError: (error) =>
          toast.error(formatApiErrorMessage("Order number not saved", error)),
      },
    );
  };

  const recordTracking = (trackingNumber: string, close: () => void) => {
    if (!trackingNumber) return;
    recordTrackingMutation.mutate(
      { actionId: action.id, trackingNumber },
      {
        onSuccess: () => {
          close();
          toast.success(`Tracking ${trackingNumber} added`);
        },
        onError: (error) =>
          toast.error(formatApiErrorMessage("Tracking not saved", error)),
      },
    );
  };

  // What the chosen reply changes on the PO when it is sent, old value struck through (USA-1658).
  const selectedChanges = selectedResolutionOption
    ? (action.card?.option_changes?.[selectedResolutionOption.option_type] ?? [])
    : [];

  const sendSelectedResolution = () => {
    if (!selectedResolutionOption || !draftBody.trim()) return;
    const cancelsPurchaseOrder =
      selectedResolutionOption.option_type === "cancel_po";
    const sendReply = () =>
      sendDraftResponseMutation.mutate(
        {
          actionId: action.id,
          bodyText: draftBody,
          resolutionType: selectedResolutionOption.option_type,
          attachments,
          recipients: replyRecipients.override,
        },
        {
          onSuccess: () => {
            setAttachments([]);
            toast.success(
              cancelsPurchaseOrder
                ? "Cancellation sent and PO canceled"
                : selectedChanges.length > 0
                  ? "Reply sent and PO updated"
                  : "Reply sent and action completed",
            );
          },
          onError: (err) =>
            toast.error(formatApiErrorMessage("Reply not sent", err), {
              description:
                "Your draft is still here. Review the message and try again.",
            }),
        },
      );

    sendReply();
  };

  return (
    <Card className={underSourceEmail || flat ? "gap-0 rounded-none border-0 bg-transparent py-0 shadow-none" : undefined}>
      <CardContent className={underSourceEmail || flat ? "space-y-3 px-0" : "space-y-3 py-4"}>
        {/* Header row: badges + summary */}
        <div
          className="flex flex-col items-stretch gap-3"
        >
          <div className="min-w-0 flex-1 space-y-2.5">
            <ActionCardTop
              card={action.card}
              actionType={action.action_type}
              status={action.status}
              inProgress={currentProgress(action) !== null}
              owners={actionOwners(action, shares)}
              due={headerDue}
              createdAt={action.created_at}
              priorityEdit={{
                source: PRIORITY_SOURCE[actionSource.kind],
                uuid: action.uuid,
              }}
            />
            <p className="text-[15px] font-semibold leading-snug break-words">
              {action.card?.headline ?? action.summary}
            </p>
            {action.card ? (
              <ActionFacts facts={action.card.facts} />
            ) : (
              linkedLineItem && (
                <span className="break-words text-xs text-muted-foreground">
                  Item #{linkedLineItem.item_number}: {linkedLineItem.model}
                </span>
              )
            )}
            {action.card?.note && (
              <p className="text-sm text-foreground/80 break-words">{action.card.note}</p>
            )}
            {action.card?.next_step && (action.status === "open" || action.status === "in_progress") && (
              <p className="text-sm break-words">
                <span className="font-medium">Next:</span>{" "}
                <span className="text-foreground/80">{action.card.next_step}</span>
              </p>
            )}
          </div>

          {/* Existing actions without suggested options still get an explicit reply path. */}
          {!isSystemOwned &&
            !isOwedOverdue &&
            !isRefundOrCredit &&
            !heldEmail &&
            !isDelayNotice &&
            !termsManufacturerUuid &&
            !isDeliverySiteReview &&
            !isLineItemActionType &&
            !isSpecCreditNoResponse &&
            !isBackorderAction &&
            !isDiscountAction &&
            !isFeeAction &&
            !action.draft_response &&
            resolutionOptions.length === 0 &&
            !inlinePanelOpen &&
            !handBooking &&
            !recordingExpediteCall &&
            !paying && (
              <div
                className="flex flex-wrap gap-2 border-t pt-3"
              >
                {payableInvoice && (
                  <Button size="sm" onClick={() => setPaying(true)} disabled={anyPending}>
                    <Check className="mr-1 h-3 w-3" /> Record payment
                  </Button>
                )}
                {emailCustomerButton}
                {bookingButtons}
                {isPoNotSent ? (
                  <>
                    {missingPoEmailFor && (
                      <ActionPopover
                        trigger={
                          <Button size="sm" disabled={anyPending}>
                            <Mail className="mr-1 h-3 w-3" /> Add PO email
                          </Button>
                        }
                        title={`PO email for ${missingPoEmailFor}`}
                        note={{
                          label: `PO email for ${missingPoEmailFor}`,
                          placeholder: "orders@manufacturer.com",
                          minLength: 1,
                          maxLength: 254,
                          singleLine: true,
                        }}
                        confirmLabel="Save and send"
                        pending={addPoEmailMutation.isPending}
                        pendingLabel="Saving..."
                        onConfirm={addPoEmail}
                      />
                    )}
                    <ActionPopover
                      trigger={
                        <Button
                          size="sm"
                          variant={missingPoEmailFor ? "outline" : "default"}
                          disabled={anyPending}
                        >
                          <Check className="mr-1 h-3 w-3" /> Mark ordered
                        </Button>
                      }
                      title="Manufacturer order #"
                      note={{
                        label: "Manufacturer order #",
                        minLength: 1,
                        maxLength: 100,
                        singleLine: true,
                      }}
                      confirmLabel="Resolve"
                      pending={recordManufacturerOrderMutation.isPending}
                      pendingLabel="Saving..."
                      onConfirm={recordManufacturerOrder}
                    />
                  </>
                ) : isTrackingMissing ? (
                  <>
                    <ActionPopover
                      trigger={
                        <Button size="sm" disabled={anyPending}>
                          <Truck className="mr-1 h-3 w-3" /> Add tracking
                        </Button>
                      }
                      title="Tracking number"
                      note={{
                        label: "Tracking number",
                        minLength: 1,
                        maxLength: 100,
                        singleLine: true,
                      }}
                      confirmLabel="Add tracking"
                      pending={recordTrackingMutation.isPending}
                      pendingLabel="Saving..."
                      onConfirm={recordTracking}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        chooseResolutionOption({ option_type: "custom_reply" })
                      }
                    >
                      <Mail className="mr-1 h-3 w-3" /> Write reply
                    </Button>
                  </>
                ) : isExpediteCall ? (
                  <Button size="sm" disabled={anyPending} onClick={() => setRecordingExpediteCall(true)}>
                    <Phone className="mr-1 h-3 w-3" /> Record call
                  </Button>
                ) : isPersonalCheckIn ? null : isSupplierCall ? (
                  <ActionPopover
                    trigger={
                      <Button size="sm" disabled={anyPending}>
                        <Phone className="mr-1 h-3 w-3" /> Record call
                      </Button>
                    }
                    title="What did they say?"
                    note={{ label: "What did they say?", minLength: 1 }}
                    confirmLabel="Record call"
                    pending={recordCallMutation.isPending}
                    pendingLabel="Recording..."
                    onConfirm={(bodyText, close) =>
                      recordCallMutation.mutate(
                        { body_text: bodyText, source: "phone_call" },
                        {
                          onSuccess: () => {
                            close();
                            toast.success("Call recorded");
                          },
                          onError: (error) =>
                            toast.error(
                              formatApiErrorMessage("Call not recorded", error),
                            ),
                        },
                      )
                    }
                  />
                ) : unsentReplyUuid ? (
                  <LoadingButton
                    size="sm"
                    loading={retryReplyMutation.isPending}
                    loadingText="Sending..."
                    disabled={anyPending}
                    onClick={() =>
                      retryReplyMutation.mutate(unsentReplyUuid, {
                        onError: (error) =>
                          toast.error(formatApiErrorMessage("Reply not sent", error)),
                      })
                    }
                  >
                    <RefreshCw className="mr-1 h-3 w-3" /> Retry send
                  </LoadingButton>
                ) : isTaxFormItem ? null : (
                  <Button
                    size="sm"
                    variant={payableInvoice ? "outline" : "default"}
                    onClick={() =>
                      chooseResolutionOption({ option_type: "custom_reply" })
                    }
                  >
                    <Mail className="mr-1 h-3 w-3" /> Write reply
                  </Button>
                )}
                {!isTaxFormReview && (
                  <DismissActionButton
                    actionId={action.id}
                    resolveAction={resolveActionMutation}
                    variant="outline"
                  >
                    <X className="mr-1 h-3 w-3" /> Dismiss
                  </DismissActionButton>
                )}
                {!isPoNotSent && !isPersonalCheckIn && (
                  <MoreFixesMenu
                    onChoose={(next) => openRecovery(next)}
                    onCancelPurchaseOrder={cancelPoInMenu}
                    disabled={anyPending}
                  />
                )}
              </div>
            )}
        </div>

        {isExpediteCall && recordingExpediteCall && action.status === "open" && (
          <ExpediteCallOutcome
            poId={poId}
            actionId={action.id}
            onCancel={() => setRecordingExpediteCall(false)}
          />
        )}

        {isOwedOverdue && action.status === "open" && (
          <ActionCardChaseActions
            actionId={action.id}
            poId={poId}
            moneyBack={action.card?.money_back ?? false}
            call={action.card?.call ?? false}
          />
        )}

        {isRefundOrCredit && action.status === "open" && (
          <RefundOrCreditChoices
            poId={poId}
            actionId={action.id}
            askAmount={typeof structuredData?.ask_amount === "string" ? structuredData.ask_amount : null}
            supplier={typeof structuredData?.manufacturer === "string" ? structuredData.manufacturer : "The supplier"}
            resolveAction={resolveActionMutation}
            disabled={anyPending}
          />
        )}

        {termsManufacturerUuid && (
          <div className="border-t pt-3">
            <PaymentTermsPanel bare uuid={termsManufacturerUuid} terms={{}} />
          </div>
        )}

        {poReviewProjectUuid && action.status === "open" && (
          <div className="border-t pt-3">
            <Button asChild size="sm">
              <Link to="/projects/$projectId" params={{ projectId: poReviewProjectUuid }}>
                Review POs
              </Link>
            </Button>
          </div>
        )}

        {isDeliverySiteReview && (
          <div className="flex flex-col items-start gap-3 border-t pt-3 sm:flex-row sm:items-center">
            <ReviewChoiceGroup
              label="Delivery site type"
              value={deliverySiteChoice}
              options={[
                { value: "commercial", label: "Commercial" },
                { value: "residential", label: "Residential" },
              ]}
              onChange={setDeliverySiteChoice}
              disabled={anyPending}
            />
            <LoadingButton
              size="sm"
              loading={resolveDeliverySiteMutation.isPending}
              loadingText="Generating PO..."
              disabled={deliverySiteChoice === null || anyPending}
              onClick={() => {
                if (deliverySiteChoice === null) return;
                resolveDeliverySiteMutation.mutate(
                  {
                    actionId: action.id,
                    residentialDelivery: deliverySiteChoice === "residential",
                  },
                  {
                    onSuccess: () =>
                      toast.success("Delivery site confirmed and PO prepared"),
                    onError: (error) =>
                      toast.error(
                        formatApiErrorMessage("PO was not queued", error),
                        {
                          description:
                            "Choose the delivery site type and try again.",
                        },
                      ),
                  },
                );
              }}
            >
              <Check className="size-3.5" aria-hidden="true" /> Confirm delivery
              site
            </LoadingButton>
          </div>
        )}

        {isSpecCreditNoResponse && (
          <div className="flex flex-wrap gap-2 border-t pt-3">
            {onSkipSpecCredit && (
              <LoadingButton
                size="sm"
                loading={skippingSpecCredit}
                loadingText="Queuing PO..."
                disabled={anyPending}
                onClick={onSkipSpecCredit}
              >
                <Send className="size-3.5" aria-hidden="true" />
                Skip spec credit &amp; send PO
              </LoadingButton>
            )}
            <Button
              size="sm"
              variant="outline"
              disabled={anyPending}
              onClick={() =>
                chooseResolutionOption({ option_type: "custom_reply" })
              }
            >
              <Mail className="size-3.5" aria-hidden="true" /> Write reply
            </Button>
            <DismissActionButton
              actionId={action.id}
              resolveAction={resolveActionMutation}
              variant="ghost"
              disabled={anyPending}
            >
              <X className="size-3.5" aria-hidden="true" /> Dismiss
            </DismissActionButton>
            <MoreFixesMenu
              onChoose={(next) => openRecovery(next)}
              onCancelPurchaseOrder={cancelPoInMenu}
              disabled={anyPending}
            />
          </div>
        )}

        {bookShipmentId !== null && handBooking && (
          <div className="border-t pt-3">
            <HandBookingForm
              poUuid={poId}
              shipmentId={bookShipmentId}
              onDone={() => setHandBooking(false)}
            />
          </div>
        )}

        {changingSource && manufacturer && (
          <div className="border-t pt-3">
            <SourceChangeForm
              poUuid={poId}
              manufacturerUuid={manufacturer.uuid}
              manufacturerName={manufacturer.name}
              actionId={action.id}
              requiredSource={requiredSource}
              onDone={() => setChangingSource(false)}
            />
          </div>
        )}

        {resolutionOptions.length > 0 &&
          !isAwaitingCustomer &&
          !emailOpen &&
          !inlinePanelOpen &&
          !handBooking &&
          !changingSource &&
          !paying && (
            <div className="space-y-2 border-t pt-3">
              <p className="text-xs font-medium text-muted-foreground">
                Choose how to handle this
              </p>
              <div className="flex flex-wrap gap-2">
                {payableInvoice && (
                  <Button size="sm" onClick={() => setPaying(true)} disabled={anyPending}>
                    <Check className="mr-1 h-3 w-3" /> Record payment
                  </Button>
                )}
                {emailCustomerButton}
                {switchToLtl && bookingButtons}
                {resolutionOptions.map((option, index) => {
                  const label =
                    option.title || ACTION_OPTION_LABELS[option.option_type] || "Review reply";
                  const variant =
                    index === 0 && !payableInvoice && !switchToLtl ? "default" : "outline";
                  // The page may carry this choice out itself, opening from the card's button.
                  const pageControl = renderOption?.(
                    option,
                    <LoadingButton size="sm" variant={variant} disabled={anyPending}>
                      {label}
                    </LoadingButton>,
                  );
                  if (pageControl !== undefined && pageControl !== null) {
                    return <Fragment key={`${option.option_type}-${index}`}>{pageControl}</Fragment>;
                  }
                  if (option.option_type === "refund_customer") {
                    return (
                      <RefundCustomerOption
                        key={`${option.option_type}-${index}`}
                        poId={poId}
                        actionId={action.id}
                        label={label}
                        variant={variant}
                        disabled={anyPending}
                      />
                    );
                  }
                  // A tax-exempt form's choice shown away from its page opens the form to decide it.
                  if (TAX_FORM_OPTION_TYPES.has(option.option_type)) {
                    return taxFormUuid ? (
                      <Button asChild key={`${option.option_type}-${index}`} size="sm" variant={variant}>
                        <Link to="/tax-exemptions/$formId" params={{ formId: taxFormUuid }}>
                          {label}
                        </Link>
                      </Button>
                    ) : null;
                  }
                  return (
                    <LoadingButton
                      key={`${option.option_type}-${index}`}
                      size="sm"
                      variant={variant}
                      loading={
                        (option.option_type === "add_delivery_services" &&
                          addDeliveryServicesMutation.isPending) ||
                        (option.option_type.startsWith("switch_to_") &&
                          freightModeMutation.isPending &&
                          freightModeMutation.variables?.freightMode ===
                            option.option_type.slice("switch_to_".length)) ||
                        (option.option_type === "keep_delivery_services_off" &&
                          resolveActionMutation.isPending &&
                          resolveActionMutation.variables?.status === "resolved")
                      }
                      onClick={() => chooseResolutionOption(option)}
                      disabled={anyPending}
                    >
                      {label}
                    </LoadingButton>
                  );
                })}
                {cancelPoButton}
                {!switchToLtl && bookingButtons}
                {!isTaxFormReview && (
                  <DismissActionButton
                    actionId={action.id}
                    resolveAction={resolveActionMutation}
                    variant="ghost"
                    disabled={anyPending}
                  >
                    <X className="mr-1 h-3 w-3" /> Dismiss
                  </DismissActionButton>
                )}
                {action.action_type !== "customer_refund" && (
                  <MoreFixesMenu
                    onChoose={(next) => openRecovery(next)}
                    onCancelPurchaseOrder={cancelPoInMenu}
                    disabled={anyPending}
                  />
                )}
              </div>
            </div>
          )}

        {(recoveryMode === "revise" || recoveryMode === "redirect") && (
          <ActionRecoveryForm
            key={recoveryMode}
            mode={recoveryMode}
            action={action}
            poId={poId}
            lineItems={lineItems}
            prefill={recoveryPrefill}
            onClose={() => setRecoveryMode(null)}
          />
        )}

        {selectedResolutionOption && (
          <div className="space-y-3 rounded-md border bg-muted/30 p-3">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <p className="text-sm font-medium">
                {selectedResolutionOption.title ||
                  ACTION_OPTION_LABELS[selectedResolutionOption.option_type] ||
                  `Reply to ${counterparty}`}
              </p>
            </div>
            <EmailRecipientsField
              state={replyRecipients}
              fallback={`The ${counterparty} on this conversation`}
              disabled={anyPending}
            />
            {selectedChanges.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-sm font-medium">PO changes</p>
                <ActionFacts facts={selectedChanges} />
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor={`resolution-reply-${action.id}`}>Reply</Label>
              <ComposeTextarea
                id={`resolution-reply-${action.id}`}
                value={draftBody}
                onChange={(event) => setDraftBody(event.target.value)}
                rows={6}
                className="min-h-[120px]"
                autoFocus
              />
              <SignoffPreview />
            </div>
            <ReplyAttachments
              attachments={attachments}
              onChange={setAttachments}
              attaching={attaching}
              onAttachingChange={setAttaching}
              disabled={anyPending}
            />
            <div className="flex flex-wrap gap-2">
              <LoadingButton
                size="sm"
                loading={sendDraftResponseMutation.isPending}
                loadingText={
                  selectedResolutionOption.option_type === "cancel_po"
                    ? "Canceling..."
                    : "Sending..."
                }
                onClick={sendSelectedResolution}
                disabled={!draftBody.trim() || anyPending}
              >
                <Send className="mr-1 h-3 w-3" />
                {selectedResolutionOption.option_type === "cancel_po"
                  ? "Send & cancel PO"
                  : selectedChanges.length > 0
                    ? "Send & update PO"
                    : "Send reply"}
              </LoadingButton>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelectedResolutionOption(null);
                  setAttachments([]);
                  replyRecipients.reset();
                }}
                disabled={anyPending}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {isDelayNotice && hasDraftReady && (
          <DelayNoticeChoice
            actionId={action.id}
            subject={action.customer_draft_subject || ""}
            template={action.customer_draft_email || ""}
            customerName={customerName}
            customerEmail={customerEmail}
            sendNotification={sendCustomerNotificationMutation}
            resolveAction={resolveActionMutation}
            disabled={anyPending}
          />
        )}

        {/* An automatic customer email held for review: send it from here (USA-1413) */}
        {heldEmail && (
          <div className="space-y-3">
            {previewOpen && <CustomerEmailPreview email={heldEmail} />}
            <div className="flex flex-wrap gap-2 border-t pt-3">
              <LoadingButton
                size="sm"
                loading={sendHeldEmail.isPending}
                loadingText="Sending..."
                disabled={anyPending}
                onClick={() =>
                  sendHeldEmail.mutate(heldEmail.message_uuid!, {
                    onSuccess: () => toast.success("Customer email sent"),
                    onError: (err) => toast.error(formatApiErrorMessage("Email not sent", err)),
                  })
                }
              >
                <Send className="mr-1 h-3 w-3" /> {HELD_EMAIL_SEND_LABELS[action.action_type] ?? "Send email"}
              </LoadingButton>
              <Button
                size="sm"
                variant="outline"
                aria-expanded={previewOpen}
                onClick={() => setPreviewOpen((open) => !open)}
              >
                <Eye className="mr-1 h-3 w-3" /> {previewOpen ? "Hide preview" : "Preview"}
              </Button>
              <DismissActionButton
                actionId={action.id}
                resolveAction={resolveActionMutation}
                variant="ghost"
                disabled={anyPending}
              >
                <X className="mr-1 h-3 w-3" /> Dismiss
              </DismissActionButton>
            </div>
          </div>
        )}

        {/* Customer approval workflow states */}
        {hasDraftReady && emailOpen && action.card?.customer_email && (
          <CustomerEmailComposer
            key={action.customer_draft_email ?? ""}
            actionId={action.id}
            email={action.card.customer_email}
            recipient={customerName || customerEmail}
            recipientEmail={customerEmail}
            sending={sendCustomerNotificationMutation.isPending}
            onCancel={() => setEmailOpen(false)}
            onSend={(bodyText, recipients) =>
              sendCustomerNotificationMutation.mutate(
                { actionId: action.id, subject: action.customer_draft_subject ?? "", bodyText, recipients },
                {
                  onSuccess: () => {
                    setEmailOpen(false);
                    toast.success("Customer email sent");
                  },
                  onError: (err) =>
                    toast.error(formatApiErrorMessage("Email not sent", err), {
                      description: "Your draft is still here. Review the message and try again.",
                    }),
                },
              )
            }
            regenerating={draftCustomerEmailMutation.isPending}
            onRegenerate={
              isPersonalCheckIn
                ? undefined
                : () =>
                    draftCustomerEmailMutation.mutate(
                      { actionId: action.id },
                      { onSuccess: () => toast.success("Draft rewritten") },
                    )
            }
            disabled={anyPending}
          />
        )}

        {isAwaitingCustomer && customerEmailStep && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <CustomerEmailSentLine email={customerEmailStep} />
            <LoadingButton
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              loading={skipCustomerApprovalMutation.isPending}
              loadingText="Skipping..."
              onClick={() => skipCustomerApprovalMutation.mutate({ actionId: action.id })}
            >
              Skip wait
            </LoadingButton>
          </div>
        )}
        {!isAwaitingCustomer && !hasCustomerDecision && customerEmailStep && (
          <CustomerEmailSentLine email={customerEmailStep} />
        )}

        {hasCustomerDecision && (
          <div className="flex items-center gap-3 rounded-md border border-green-200 bg-green-50/50 p-3">
            <UserCheck className="h-4 w-4 text-green-600" />
            <div className="flex-1">
              <p className="text-sm font-medium">
                Customer decision:{" "}
                <Badge
                  variant={
                    action.customer_decision === "approved"
                      ? "default"
                      : action.customer_decision === "rejected"
                        ? "destructive"
                        : "secondary"
                  }
                >
                  {action.customer_decision === "approved"
                    ? "Approved"
                    : action.customer_decision === "rejected"
                      ? "Rejected"
                      : "Alternative suggested"}
                </Badge>
              </p>
              {action.customer_decision_details && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {(() => {
                    try {
                      const d = JSON.parse(action.customer_decision_details);
                      return typeof d === "object" && d !== null
                        ? (d.details as string) || ""
                        : "";
                    } catch {
                      return action.customer_decision_details;
                    }
                  })()}
                </p>
              )}
            </div>
            {action.customer_communication_thread_id && (
              <Link
                to="/customer-comms/$threadId"
                params={{
                  threadId: String(action.customer_communication_thread_id!),
                }}
                className="text-xs text-blue-600 hover:underline"
              >
                View thread
              </Link>
            )}
          </div>
        )}

        {payableInvoice && paying && (
          <div className="border-t pt-3">
            <RecordPaymentPanel
              invoice={payableInvoice}
              actionSource="purchase_order"
              actionId={action.id}
              onCancel={() => setPaying(false)}
            />
          </div>
        )}

        {/* AI-drafted response to manufacturer */}
        {action.draft_response &&
          action.status === "open" &&
          !paying &&
          resolutionOptions.length === 0 && (
            showDraftReply ? (
            <div className="space-y-3 rounded-md border bg-muted/30 p-3">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm font-medium">Reply to {counterparty}</p>
              </div>
              <EmailRecipientsField
                state={replyRecipients}
                fallback={`The ${counterparty} on this conversation`}
                disabled={anyPending}
              />
              <div className="space-y-1">
                <label
                  htmlFor={`manufacturer-draft-${action.id}`}
                  className="text-xs text-muted-foreground"
                >
                  Email body
                </label>
                <ComposeTextarea
                  id={`manufacturer-draft-${action.id}`}
                  value={draftBody}
                  onChange={(e) => setDraftBody(e.target.value)}
                  rows={6}
                  className="min-h-[120px]"
                />
                <SignoffPreview />
              </div>
              <ReplyAttachments
                attachments={attachments}
                onChange={setAttachments}
                attaching={attaching}
                onAttachingChange={setAttaching}
                disabled={anyPending}
              />
              <div className="flex flex-wrap gap-2">
                <LoadingButton
                  size="sm"
                  loading={sendDraftResponseMutation.isPending}
                  loadingText="Sending..."
                  onClick={() =>
                    sendDraftResponseMutation.mutate(
                      {
                        actionId: action.id,
                        bodyText: draftBody,
                        attachments,
                        recipients: replyRecipients.override,
                      },
                      {
                        onSuccess: () => {
                          setAttachments([]);
                          toast.success("Reply sent and action resolved");
                        },
                        onError: (err) =>
                          toast.error(
                            formatApiErrorMessage("Email not sent", err),
                            {
                              description:
                                "Your draft is still here. Review the message and try again.",
                            },
                          ),
                      },
                    )
                  }
                  disabled={!draftBody.trim() || anyPending}
                >
                  <Send className="mr-1 h-3 w-3" /> Approve & Send
                </LoadingButton>
                <DismissActionButton
                  actionId={action.id}
                  resolveAction={resolveActionMutation}
                  variant="outline"
                  disabled={anyPending}
                >
                  <X className="mr-1 h-3 w-3" /> Dismiss
                </DismissActionButton>
                <MoreFixesMenu
                  onChoose={(next) => openRecovery(next)}
                  onCancelPurchaseOrder={cancelPoInMenu}
                  disabled={anyPending}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowDraftReply(false)}
                  disabled={anyPending}
                >
                  Cancel
                </Button>
              </div>
            </div>
            ) : (
              <div className="flex flex-wrap gap-2 border-t pt-3">
                {payableInvoice && (
                  <Button size="sm" onClick={() => setPaying(true)} disabled={anyPending}>
                    <Check className="mr-1 h-3 w-3" /> Record payment
                  </Button>
                )}
                <Button
                  size="sm"
                  variant={payableInvoice ? "outline" : "default"}
                  onClick={() => setShowDraftReply(true)}
                >
                  <Mail className="mr-1 h-3 w-3" /> Review drafted reply
                </Button>
                <DismissActionButton
                  actionId={action.id}
                  resolveAction={resolveActionMutation}
                  variant="outline"
                  disabled={anyPending}
                >
                  <X className="mr-1 h-3 w-3" /> Dismiss
                </DismissActionButton>
                <MoreFixesMenu
                  onChoose={(next) => openRecovery(next)}
                  onCancelPurchaseOrder={cancelPoInMenu}
                  disabled={anyPending}
                />
              </div>
            )
          )}

        {/* Backorder: Approve delay is primary action */}
        {isBackorderAction &&
          !inlinePanelOpen &&
          !isAwaitingCustomer &&
          !emailOpen &&
          resolutionOptions.length === 0 && (
            <div className="flex flex-wrap gap-2 border-t pt-3">
              {emailCustomerButton}
              <LoadingButton
                size="sm"
                loading={approveBackorderMutation.isPending}
                loadingText="Approving..."
                onClick={() =>
                  approveBackorderMutation.mutate(
                    { actionId: action.id },
                    { onSuccess: () => setMode(null) },
                  )
                }
                disabled={anyPending}
              >
                <Check className="mr-1 h-3 w-3" /> Approve delay
              </LoadingButton>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setMode("remove")}
                disabled={anyPending}
              >
                <Trash2 className="mr-1 h-3 w-3" /> Remove item & refund
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setMode("replace")}
                disabled={anyPending}
              >
                <ArrowRightLeft className="mr-1 h-3 w-3" /> Find replacement
              </Button>
              {cancelPoButton}
              <DismissActionButton
                actionId={action.id}
                resolveAction={resolveActionMutation}
                variant="ghost"
                disabled={anyPending}
              >
                <X className="mr-1 h-3 w-3" /> Dismiss
              </DismissActionButton>
              <MoreFixesMenu
                onChoose={(next) => openRecovery(next)}
                onCancelPurchaseOrder={cancelPoInMenu}
                disabled={anyPending}
              />
            </div>
          )}

        {/* Structured resolution options for discontinued/substitution actions */}
        {isLineItemActionType &&
          !inlinePanelOpen &&
          !isAwaitingCustomer &&
          !emailOpen &&
          resolutionOptions.length === 0 && (
            <div className="flex flex-wrap gap-2 border-t pt-3">
              {emailCustomerButton}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setMode("remove")}
                disabled={anyPending}
              >
                <Trash2 className="mr-1 h-3 w-3" /> Remove item & refund
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setMode("replace")}
                disabled={anyPending}
              >
                <ArrowRightLeft className="mr-1 h-3 w-3" /> Find replacement
              </Button>
              {cancelPoButton}
              <DismissActionButton
                actionId={action.id}
                resolveAction={resolveActionMutation}
                variant="ghost"
                disabled={anyPending}
              >
                <X className="mr-1 h-3 w-3" /> Dismiss
              </DismissActionButton>
              <MoreFixesMenu
                onChoose={(next) => openRecovery(next)}
                onCancelPurchaseOrder={cancelPoInMenu}
                disabled={anyPending}
              />
            </div>
          )}

        {/* Structured resolution for discount/price actions */}
        {isDiscountAction &&
          !inlinePanelOpen &&
          !isAwaitingCustomer &&
          !emailOpen &&
          resolutionOptions.length === 0 && (
            <div className="flex flex-wrap gap-2 border-t pt-3">
              {emailCustomerButton}
              <Button
                size="sm"
                onClick={() => setMode("discount")}
                disabled={anyPending}
              >
                <Percent className="mr-1 h-3 w-3" /> Accept new pricing
              </Button>
              <DismissActionButton
                actionId={action.id}
                resolveAction={resolveActionMutation}
                variant="ghost"
                disabled={anyPending}
              >
                <X className="mr-1 h-3 w-3" /> Dismiss
              </DismissActionButton>
              <MoreFixesMenu
                onChoose={(next) => openRecovery(next)}
                onCancelPurchaseOrder={cancelPoInMenu}
                disabled={anyPending}
              />
            </div>
          )}

        {/* Structured resolution for fee/charge actions */}
        {isFeeAction &&
          !inlinePanelOpen &&
          !isAwaitingCustomer &&
          !emailOpen &&
          resolutionOptions.length === 0 && (
            <div className="flex flex-wrap gap-2 border-t pt-3">
              {emailCustomerButton}
              <Button
                size="sm"
                variant="outline"
                onClick={() => setMode("fee")}
                disabled={anyPending}
              >
                <DollarSign className="mr-1 h-3 w-3" /> Accept fee
              </Button>
              <DismissActionButton
                actionId={action.id}
                resolveAction={resolveActionMutation}
                variant="ghost"
                disabled={anyPending}
              >
                <X className="mr-1 h-3 w-3" /> Dismiss
              </DismissActionButton>
              <MoreFixesMenu
                onChoose={(next) => openRecovery(next)}
                onCancelPurchaseOrder={cancelPoInMenu}
                disabled={anyPending}
              />
            </div>
          )}

        {/* Accept fee, remove item, discount, freight and cancellation open here (USA-1536). */}
        <ActionPopover
          anchorOnly
          trigger={<div aria-hidden="true" className="h-0" />}
          open={popoverMode !== null}
          onOpenChange={(open) => {
            if (!open) closePopover();
          }}
          {...(popover ?? {})}
        />

        {/* Replacement Search */}
        {mode === "replace" && (
          <div className="space-y-3 rounded-md border bg-muted/30 p-3">
            <p className="text-sm font-medium">Find a replacement product</p>
            {!linkedLineItem && lineItems && lineItems.length > 0 ? (
              <>
                <p className="text-xs text-muted-foreground">
                  Which line item is being replaced?
                </p>
                <div className="space-y-1">
                  {lineItems.map((li) => (
                    <button
                      key={li.id}
                      onClick={() => setSelectedLineItemId(li.id)}
                      className={`w-full text-left rounded-md border px-3 py-2 text-sm hover:bg-accent ${selectedLineItemId === li.id ? "border-primary bg-primary/5" : ""}`}
                    >
                      #{li.item_number}: {li.model} — qty {li.quantity},{" "}
                      {formatMoney(li.unit_price)}/ea
                    </button>
                  ))}
                </div>
              </>
            ) : linkedLineItem ? (
              <p className="text-xs text-muted-foreground">
                Current: {linkedLineItem.model} at{" "}
                {formatMoney(linkedLineItem.unit_price)}/ea
              </p>
            ) : null}
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <SearchInput
                placeholder="Search by model or description..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>
            {candidatesLoading && <Skeleton className="h-20 w-full" />}
            {candidates && candidates.length > 0 && (
              <div className="max-h-48 overflow-y-auto rounded border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Model</TableHead>
                      <TableHead>List</TableHead>
                      <TableHead>Net</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {candidates.map((c) => (
                      <TableRow
                        key={c.id}
                        className={
                          selectedProductId === c.id
                            ? "bg-primary/10"
                            : "cursor-pointer"
                        }
                        onClick={() => setSelectedProductId(c.id)}
                      >
                        <TableCell className="text-xs">
                          <div>{c.model}</div>
                          {c.specifications && (
                            <div className="text-muted-foreground">
                              {c.specifications}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {c.list_price ? formatMoney(c.list_price) : "-"}
                        </TableCell>
                        <TableCell className="text-xs">
                          {c.net_price ? formatMoney(c.net_price) : "-"}
                        </TableCell>
                        <TableCell>
                          {selectedProductId === c.id && (
                            <Check className="h-4 w-4 text-primary" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {candidates && candidates.length === 0 && debouncedSearch && (
              <p className="text-xs text-muted-foreground">No products found</p>
            )}
            {selectedProductId && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-muted-foreground">
                    Price override (optional):
                  </label>
                  <InlineFieldInput
                    type="number"
                    step="0.01"
                    placeholder="Leave blank to use net price"
                    value={priceOverride}
                    onChange={(e) => setPriceOverride(e.target.value)}
                    className="w-40"
                  />
                </div>
                <ComposeTextarea
                  placeholder="Notes (optional)"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                />
              </div>
            )}
            <div className="flex gap-2">
              <LoadingButton
                size="sm"
                loading={replaceMutation.isPending}
                loadingText="Replacing..."
                onClick={() =>
                  replaceMutation.mutate(
                    {
                      actionId: action.id,
                      lineItemId: selectedLineItemId ?? undefined,
                      replacementProductId: selectedProductId!,
                      replacementUnitPrice: priceOverride
                        ? Number(priceOverride)
                        : undefined,
                      notes: notes || undefined,
                    },
                    { onSuccess: () => setMode(null) },
                  )
                }
                disabled={!selectedProductId || anyPending}
              >
                Confirm replacement
              </LoadingButton>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setMode(null)}
                disabled={anyPending}
              >
                Back
              </Button>
            </div>
          </div>
        )}

        {/* Request Customer Approval — on any open PO action that doesn't already have it */}
        {counterparty === "manufacturer" &&
          itemSource.kind !== "manufacturer_data" &&
          itemSource.kind !== "warranty_claim" &&
          action.action_type !== "po_not_sent" &&
          action.action_type !== "email_delivery_failed" &&
          !isExpediteCall &&
          !isOwedOverdue &&
          !isRefundOrCredit &&
          !isSystemOwned &&
          !termsManufacturerUuid &&
          !isDeliverySiteReview &&
          !action.customer_approval_required &&
          !inlineMode && (
            <div className="border-t pt-2">
              <LoadingButton
                size="sm"
                variant="ghost"
                className="text-xs text-muted-foreground"
                loading={draftCustomerEmailMutation.isPending}
                loadingText="Drafting..."
                onClick={() =>
                  draftCustomerEmailMutation.mutate(
                    { actionId: action.id },
                    {
                      onSuccess: () => toast.success("Customer email draft generated"),
                      onError: (err) =>
                        toast.error(`Failed: ${(err as Error).message}`),
                    },
                  )
                }
                disabled={anyPending}
              >
                <Mail className="mr-1 h-3 w-3" /> Request Customer Approval
              </LoadingButton>
            </div>
          )}
        <ActionWorkSection action={action} poId={poId} comments={comments} canStart={!isSystemOwned} />
        <div className="space-y-1 border-t pt-2.5 text-xs text-muted-foreground">
          <p className="break-words">
            <span className="font-medium text-foreground">Raised</span>
            {raisedBy && ` by ${raisedBy}`} · {formatEventTime(action.created_at)}
            {action.card?.source_email && !underSourceEmail && (
              <>
                {" · from "}
                <SourceEmailLink email={action.card.source_email} poId={poId} />
              </>
            )}
            {action.card?.shipment && (
              <>
                {" · about "}
                <SourceShipmentLink shipment={action.card.shipment} />
              </>
            )}
            {action.card?.invoice && (
              <>
                {" · about "}
                <ActionInvoiceLink invoice={action.card.invoice} />
              </>
            )}
          </p>
          {actionAuditEvents(action, actorName, reopens)
            .filter((event) => event.kind !== "raised")
            .map((event) => (
              <p key={event.key}>
                <ActionAuditLine event={event} />
              </p>
            ))}
          <ActionDetails summary={action.summary} details={action.details} headline={action.card?.headline ?? action.summary} />
        </div>
      </CardContent>
    </Card>
  );
}

function ClosedActionRow({
  action,
  poId,
  reopens,
  shares,
  actorName,
}: {
  action: PurchaseOrderAction;
  poId: string;
  reopens: readonly PurchaseOrderActionReopen[];
  shares: readonly PurchaseOrderActionShare[];
  actorName: (value: string | null | undefined) => string | null;
}) {
  const reopenAction = useReopenAction(poId);
  const notes = actionResolutionNotes(action);
  const showResolutionType =
    action.resolution_type &&
    !["dismissed", "manual"].includes(action.resolution_type);
  return (
    <li className="space-y-1.5 px-4 py-3" data-testid="closed-action">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-semibold">
            {action.card?.kind_label ?? formatStatus(action.action_type)}
            {action.card?.sublabel && (
              <span className="font-normal text-muted-foreground">
                {" "}
                · {action.card.sublabel}
              </span>
            )}
          </span>
          <span className="text-xs text-muted-foreground">
            {actionOwners(action, shares).join(", ")}
          </span>
          {showResolutionType && (
            <Badge variant="outline" className="text-xs">
              {CLOSED_RESOLUTION_LABELS[action.resolution_type ?? ""] ??
                formatStatus(action.resolution_type)}
            </Badge>
          )}
        </div>
        {/* Sales's "Review POs" (USA-1333) closed when its POs were sent; there is nothing to reopen. */}
        {action.action_type !== "po_review" && (
          <LoadingButton
            size="sm"
            variant="ghost"
            className="-my-1 shrink-0"
            loading={reopenAction.isPending}
            loadingText="Reopening..."
            onClick={() =>
              reopenAction.mutate(action.id, {
                onError: (error) =>
                  toast.error(
                    formatApiErrorMessage("Action not reopened", error),
                  ),
              })
            }
          >
            <RotateCcw className="size-3.5" aria-hidden="true" /> Reopen
          </LoadingButton>
        )}
      </div>
      <p className="text-sm font-medium break-words">
        {action.card?.headline ?? action.summary}
      </p>
      {notes && (
        <p className="text-sm text-muted-foreground break-words">{notes}</p>
      )}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {actionAuditEvents(action, actorName, reopens).map((event) => (
          <ActionAuditLine key={event.key} event={event} />
        ))}
      </div>
      {action.card && (
        <ActionDetails summary={action.summary} details={action.details} headline={action.card?.headline ?? action.summary} />
      )}
    </li>
  );
}

export function POActionsSection({
  canSkipSpecCredit,
  handleSkipSpecCredit,
  skippingSpecCredit,
  po,
  poId,
  showResolvedActions,
  setShowResolvedActions,
  openActions,
  resolvedActions,
}: {
  canSkipSpecCredit: boolean;
  handleSkipSpecCredit: () => void;
  skippingSpecCredit: boolean;
  po: PurchaseOrderDetail;
  poId: string;
  showResolvedActions: boolean;
  setShowResolvedActions: Dispatch<SetStateAction<boolean>>;
  openActions: NonNullable<PurchaseOrderDetail["actions"]>;
  resolvedActions: NonNullable<PurchaseOrderDetail["actions"]>;
}) {
  const lineItems = po.line_items.filter(
    (lineItem) => !isManufacturerSurchargeLine(lineItem),
  );
  // Ops's items on this PO's invoices (approvals and the like), so they are found here too (USA-1641).
  const { data: invoiceActions = [] } = usePurchaseOrderInvoiceActions(poId);
  return (
    <>
      {/* Actions */}
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Actions
          </h3>
          <AddActionPopover poUuid={poId} lineItems={lineItems} />
        </div>
        <ActionItemsList
          poId={poId}
          comments={poActionComments(po)}
          freightMode={cardFreightMode(po)}
          manufacturer={poManufacturer(po)}
          openActions={openActions}
          resolvedActions={resolvedActions}
          reopens={po.action_reopens}
          shares={po.action_shares}
          lineItems={lineItems}
          customerName={po.internal_order?.customer_name}
          customerEmail={po.internal_order?.customer_email}
          canSkipSpecCredit={canSkipSpecCredit}
          onSkipSpecCredit={handleSkipSpecCredit}
          skippingSpecCredit={skippingSpecCredit}
          showResolvedActions={showResolvedActions}
          setShowResolvedActions={setShowResolvedActions}
          afterOpen={<POInvoiceActionRows actions={invoiceActions} />}
          showEmptyState={invoiceActions.length === 0}
        />
      </div>
    </>
  );
}

/**
 * Open action cards, the folded resolved & dismissed list with each item's history, and the
 * empty state. The PO page shows its actions with it, and so does a customer email thread
 * (inside an ActionItemSourceProvider, USA-1328).
 */
/** The full card for one of a PO's action items, for the email that raised it (USA-1417). */
export function PurchaseOrderActionCard({
  po,
  action,
}: {
  po: PurchaseOrderDetail;
  action: PurchaseOrderAction;
}) {
  return (
    <StructuredActionCard
      action={action}
      poId={po.uuid}
      freightMode={cardFreightMode(po)}
      manufacturer={poManufacturer(po)}
      lineItems={po.line_items.filter(
        (lineItem) => !isManufacturerSurchargeLine(lineItem),
      )}
      customerName={po.internal_order?.customer_name}
      customerEmail={po.internal_order?.customer_email}
      reopens={po.action_reopens}
      shares={po.action_shares}
      comments={poActionComments(po)}
      underSourceEmail
    />
  );
}

/** One customer action item on the flat card shown under the customer email that raised it (USA-1413). */
export function CustomerActionCard({
  action,
  reopens,
  customerName,
  customerEmail,
  comments,
}: {
  action: PurchaseOrderAction;
  reopens: readonly PurchaseOrderActionReopen[];
  customerName?: string | null;
  customerEmail?: string | null;
  comments?: ActionCommentsContext;
}) {
  return (
    <StructuredActionCard
      action={action}
      poId=""
      lineItems={[]}
      reopens={reopens}
      shares={[]}
      customerName={customerName}
      customerEmail={customerEmail}
      comments={comments}
      underSourceEmail
    />
  );
}

/** The manufacturer a PO's source change picks a source of (USA-1506). */
function poManufacturer(po: PurchaseOrderDetail): { uuid: string; name: string } | undefined {
  return po.manufacturer?.uuid
    ? { uuid: po.manufacturer.uuid, name: po.manufacturer.short_name || po.manufacturer.name }
    : undefined;
}

/** A PO's action items comment in its project's comments, tagged with the PO (USA-1443). */
function poActionComments(po: PurchaseOrderDetail): ActionCommentsContext | undefined {
  const projectUuid = po.internal_order?.project?.uuid;
  return projectUuid
    ? { projectUuid, poUuid: po.uuid, poNumber: po.po_number }
    : undefined;
}

export function ActionItemsList({
  poId,
  freightMode,
  manufacturer,
  openActions,
  resolvedActions,
  reopens,
  shares,
  lineItems,
  customerName,
  customerEmail,
  canSkipSpecCredit = false,
  onSkipSpecCredit,
  skippingSpecCredit,
  showResolvedActions,
  setShowResolvedActions,
  showEmptyState = true,
  afterOpen,
  comments,
  flat = false,
}: {
  poId: string;
  comments?: ActionCommentsContext;
  freightMode?: "parcel" | "ltl" | null;
  manufacturer?: { uuid: string; name: string };
  openActions: readonly PurchaseOrderAction[];
  resolvedActions: readonly PurchaseOrderAction[];
  reopens: readonly PurchaseOrderActionReopen[];
  shares: readonly PurchaseOrderActionShare[];
  lineItems: components["schemas"]["PurchaseOrderDetailRead"]["line_items"];
  customerName?: string | null;
  customerEmail?: string | null;
  canSkipSpecCredit?: boolean;
  onSkipSpecCredit?: () => void;
  skippingSpecCredit?: boolean;
  showResolvedActions: boolean;
  setShowResolvedActions: Dispatch<SetStateAction<boolean>>;
  showEmptyState?: boolean;
  /** Shown after the open cards, before the resolved fold. */
  afterOpen?: ReactNode;
  /** Cards and the resolved list without boxes of their own, inside a row that is one (USA-915). */
  flat?: boolean;
}) {
  const actorName = useActorName();
  return (
    <>
      <div className={flat ? "divide-y" : "space-y-3"}>
        {openActions.map((action) => (
          <div
            key={action.id}
            {...actionTargetProps(action.uuid)}
            className={cn(`rounded-xl ${ACTION_FOCUS_CLASS}`, flat && "py-3 first:pt-0 last:pb-0")}
          >
            <StructuredActionCard
              action={action}
              poId={poId}
              freightMode={freightMode}
              manufacturer={manufacturer}
              lineItems={lineItems}
              customerName={customerName}
              customerEmail={customerEmail}
              onSkipSpecCredit={canSkipSpecCredit ? onSkipSpecCredit : undefined}
              skippingSpecCredit={skippingSpecCredit}
              reopens={reopens}
              shares={shares}
              comments={comments}
              flat={flat}
            />
          </div>
        ))}
      </div>
      {afterOpen}
      {resolvedActions.length > 0 && (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => setShowResolvedActions((v) => !v)}
            className="flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
            aria-expanded={showResolvedActions}
          >
            {showResolvedActions ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            Resolved &amp; dismissed ({resolvedActions.length})
          </button>
          {showResolvedActions && (
            <ul
              className={flat ? "divide-y [&>li]:px-0" : "divide-y rounded-lg border"}
              aria-label="Resolved and dismissed actions"
            >
              {[...resolvedActions]
                .sort((a, b) =>
                  (b.resolved_at ?? b.updated_at).localeCompare(
                    a.resolved_at ?? a.updated_at,
                  ),
                )
                .map((action) => (
                  <ClosedActionRow
                    key={action.id}
                    action={action}
                    poId={poId}
                    reopens={reopens}
                    shares={shares}
                    actorName={actorName}
                  />
                ))}
            </ul>
          )}
        </div>
      )}
      {openActions.length + resolvedActions.length === 0 && showEmptyState && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <CheckCircle2 className="h-4 w-4 text-green-500" />
          No actions required at this time
        </div>
      )}
    </>
  );
}
